/*    
<summary>
  This functional component "AddCategoryManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../../contexts/store-provider";
import { IAddHeadquarter } from "../../../models/response/IHeadquarterResponse";
import { formatMessage } from "../../../translations/format-message";
import AddEditHeadquarterManagementForm from "../forms/add-edit-headquarter-form";

interface IProps {
  id: number;
  modalClosed: () => void;
}

const AddEditHeadquarterManagement = (props: IProps) => {
  const { masterManagementStore, headquarterStore } = useStore();
  const { GetAllBusinessTypeService, getAllBusinessTypes } =
    masterManagementStore;
  const {
    AddHeadquarterService,
    UpdateHeadquarterService,
    getHeadquartertByIdState,
    addUpdateHeadquartertState,
    resetGetHeadquarterDetail,
    resetAddUpdateHeadquarter,
    GetHeadquarterByIdService,
    headquarterDetails,
    DeleteHeadquarterService,
    deleteHeadquarterByIdState,
    resetDeleteHeadquarter,
  } = headquarterStore;
  const initialValues: IAddHeadquarter = headquarterDetails;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllBusinessTypeService();
    if (props.id > 0) {
      GetHeadquarterByIdService(props.id);
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateHeadquartertState.success) {
      if (props.id > 0) toast.success(formatMessage("updated_success"));
      else toast.success(formatMessage("added_success"));
      onModalClosed();
    }
  }, [addUpdateHeadquartertState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "staffDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (getHeadquartertByIdState.error) {
      toast.error(formatMessage(getHeadquartertByIdState.error));
    }
  }, [getHeadquartertByIdState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateHeadquartertState.error) {
      toast.error(formatMessage(addUpdateHeadquartertState.error));
      resetAddUpdateHeadquarter();
    }
  }, [addUpdateHeadquartertState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteHeadquarterByIdState.success) {
      toast.success(formatMessage("deleted_success"));
      onDeleteModalClosed();
    }
  }, [deleteHeadquarterByIdState.success]);

  useEffect(() => {
    if (deleteHeadquarterByIdState.error) {
      toast.error(formatMessage(deleteHeadquarterByIdState.error));
      onDeleteModalClosed();
    }
  }, [deleteHeadquarterByIdState.error]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    if (props.id > 0) UpdateHeadquarterService(values);
    else AddHeadquarterService(values);
  };

  const handleDeleteSubmitHandler = () => {
    DeleteHeadquarterService(props.id);
  };

  /**
   * This function closes the pop up modal and reset getstoredetails, addUpdate store observables (success, inprogress
   * and error).
   */
  const onDeleteModalClosed = () => {
    resetGetHeadquarterDetail();
    resetDeleteHeadquarter();
    props.modalClosed();
  };

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetGetHeadquarterDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddEditHeadquarterManagementForm
        id={props.id}
        businessType={getAllBusinessTypes}
        initialValues={initialValues}
        onModalClosed={onModalClosed}
        submitHandler={onSubmitForm}
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        onDeleteModalClosed={onDeleteModalClosed}
        isLoading={
          addUpdateHeadquartertState.inProgress ||
          getHeadquartertByIdState.inProgress ||
          deleteHeadquarterByIdState.inProgress
        }
      />
    </React.Fragment>
  );
};

export default observer(AddEditHeadquarterManagement);
